import * as React from 'react';
import { InitConfiguration } from '@datadog/browser-core';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import './index.css';

import { notification as duxtonNotification, theme } from '@duxton-web/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, notification } from 'antd';
import englishLocale from 'antd/locale/en_US';

import {
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_TOKEN,
  isProduction,
  isStaging,
  redirectTo,
} from './common/environment';
import { getTenant } from './common/Tenants';
import { SystemEnvironment } from './constants/systemEnviroment';
import AppProvider from './contexts/app-context';
import Service, {
  CacheService,
  DefaultHttpService,
  GrabAreaManagementService,
  PlannerService,
} from './lib/App';
import { SAMLService } from './lib/SAMLService';
import { XPortalAppRoutes } from './pages/XPortalAppRoutes';
// import history from './services/History'; // Removed in v6
import WithUpdateWorker from './services/update-worker/WithUpdateWorker';
import configureStore from './store';
import { getSystemEnvironment } from './utils/system-environment';

let envParam: SystemEnvironment = getSystemEnvironment();

const HttpService = new DefaultHttpService();
Service.register('CacheService', new CacheService());
Service.register('PlannerService', new PlannerService(HttpService));
Service.register(
  'GrabAreaManagementService',
  new GrabAreaManagementService(HttpService),
);
Service.register('SAMLService', new SAMLService(HttpService));
Service.register('HttpService', HttpService);

const { store, persistor } = configureStore();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

if (isStaging && redirectTo && Service.planner!.isLoggedIn()) {
  Service.planner!.logout();
}

// Legacy reason. Will migrate AntD notification to Duxton UI notification in all places.
notification.config({
  placement: 'bottomLeft',
  duration: 5,
});
duxtonNotification.config({
  placement: 'bottomLeft',
  duration: 5,
});

function hideError(e) {
  const errorMessages = [
    "Uncaught NetworkError: Failed to execute 'importScripts' on 'WorkerGlobalScope': The script at 'http://localhost:3000/static/js/worker-json.js' failed to load.",
    'ResizeObserver loop completed with undelivered notifications.',
  ];
  if (errorMessages.includes(e.message)) {
    const resizeObserverErrDiv = document.getElementById(
      'webpack-dev-server-client-overlay-div',
    );
    const resizeObserverErr = document.getElementById(
      'webpack-dev-server-client-overlay',
    );
    if (resizeObserverErr) {
      resizeObserverErr.setAttribute('style', 'display: none');
    }
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute('style', 'display: none');
    }
  }
}

window.addEventListener('error', hideError);

async function enableMocking() {
  if (process.env.WEB_APP_LOCAL_MODE !== 'true') {
    return;
  }

  const { worker } = await import('./e2e/mocks/browser');

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start();
}

const AppWithWorker = WithUpdateWorker(XPortalAppRoutes);

enableMocking().then(async () => {
  // Render the DOM with React 18 createRoot API
  const container = document.getElementById('root');
  if (!container) throw new Error('Failed to find the root element');

  const root = createRoot(container);
  root.render(
    <AppProvider value={envParam}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={theme.defaultConfig} locale={englishLocale}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <AppWithWorker />
            </PersistGate>
          </Provider>
        </ConfigProvider>
      </QueryClientProvider>
    </AppProvider>,
  );
});

if (process.env.WEB_APP_LOCAL_MODE !== 'true') {
  const datadogCommonConfig: Pick<
    InitConfiguration,
    'clientToken' | 'site' | 'sessionSampleRate' | 'service' | 'env' | 'version'
  > = {
    clientToken: DATADOG_TOKEN,
    site: 'datadoghq.com',
    sessionSampleRate: 100,
    service: 'x-portal',
    env: isStaging ? 'staging' : isProduction ? 'production' : 'development',
    version: process.env.WEB_APP_VERSION,
  };

  datadogLogs.init({
    ...datadogCommonConfig,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'debug'],
  });

  datadogRum.init({
    ...datadogCommonConfig,
    applicationId: DATADOG_RUM_APPLICATION_ID,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'allow',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
  });

  datadogRum.setViewContextProperty('tenant', getTenant());
}
